import { useEffect, useState } from 'react'
import type { CombatEventDto, CombatReplayDto } from '@/api/hooks'
import { CombatPrimer } from '@/components/CombatPrimer'

const PHASE_BLURB: Record<CombatEventDto['phase'], string> = {
  manoeuvre: 'Le camp qui touche le plus gagne l\'avantage du vent et tire en premier.',
  bordee: 'Artillerie à distance, les coques encaissent.',
  abordage: 'Corps à corps simultané sur les survivants.',
}

const END_REASON_LABEL: Record<string, string> = {
  defender_wiped: 'Défenses enfoncées',
  attacker_wiped: 'Flotte attaquante détruite',
  mutual_destruction: 'Destruction mutuelle',
  defender_holds: 'Les défenseurs ont tenu',
}

type Perspective = 'attacker' | 'defender'

/**
 * Label for a combatant side, relative to WHO is watching the replay. The
 * `combat` payload is absolute (attacker/defender); the viewer is the attacker
 * on an offensive raid report and the defender on a received-raid report.
 */
function sideLabel(side: CombatEventDto['side'], perspective: Perspective): string {
  if (side === perspective) return perspective === 'attacker' ? 'Ta flotte' : 'Ta défense'
  return perspective === 'attacker' ? 'La défense' : 'La flotte adverse'
}

/**
 * Phase-by-phase replay of a fleet battle (M6-A5). Auto-advances through the
 * serialised CombatEventDto log, revealing dice rolls and casualties one event
 * at a time. The player can pause, step, or skip to the end.
 */
export function CombatReplay({
  combat,
  perspective = 'attacker',
  onClose,
}: {
  combat: CombatReplayDto
  perspective?: Perspective
  onClose: () => void
}) {
  const events = combat.events
  const [step, setStep] = useState(0) // number of events revealed
  const [playing, setPlaying] = useState(true)

  const done = step >= events.length

  useEffect(() => {
    if (!playing || done) return
    const id = window.setTimeout(() => setStep((s) => s + 1), 1300)
    return () => window.clearTimeout(id)
  }, [playing, done, step])

  const revealed = events.slice(0, step)
  const winnerLabel =
    combat.winnerSide === 'draw'
      ? 'Match nul'
      : combat.winnerSide === perspective
        ? 'Victoire'
        : 'Défaite'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ocean-900/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-ocean-800 border border-ocean-500/40 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-ocean-800/95 backdrop-blur border-b border-ocean-700 px-5 py-3 flex items-center justify-between">
          <h2 className="font-display text-xl text-sand-300">⚔️ Récit du combat</h2>
          <button
            onClick={onClose}
            className="text-ocean-300 hover:text-sand-100 transition text-xl leading-none"
            aria-label="Fermer"
          >
            ×
          </button>
        </header>

        <div className="p-5 space-y-3">
          <CombatPrimer />

          {revealed.length === 0 && (
            <p className="text-center text-ocean-300 italic py-8">Les flottes se font face…</p>
          )}

          {revealed.map((event, i) => {
            const isRoundStart = i === 0 || revealed[i - 1].round !== event.round
            // Show the phase blurb once per phase within a round.
            const isPhaseStart = i === 0 || revealed[i - 1].phase !== event.phase || isRoundStart
            return (
              <div key={i} className="space-y-1">
                {isRoundStart && (
                  <div className="flex items-center gap-2 pt-2">
                    <span className="text-xs font-display text-sand-300 uppercase tracking-widest">
                      Round {event.round}
                    </span>
                    <span className="flex-1 h-px bg-ocean-600/50" />
                  </div>
                )}
                {isPhaseStart && (
                  <p className="text-[11px] text-ocean-400 italic px-1">{PHASE_BLURB[event.phase]}</p>
                )}
                <EventRow event={event} perspective={perspective} isLatest={i === revealed.length - 1 && !done} />
              </div>
            )
          })}

          {done && (
            <div className="mt-4 rounded-lg border border-sand-300/30 bg-ocean-900/50 p-4 text-center space-y-1">
              <p className="font-display text-2xl text-sand-300">{winnerLabel}</p>
              <p className="text-sm text-ocean-200">
                {END_REASON_LABEL[combat.endReason] ?? combat.endReason}
              </p>
              <FleetOutcome combat={combat} perspective={perspective} />
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-ocean-800/95 backdrop-blur border-t border-ocean-700 px-5 py-3 flex items-center gap-3">
          {!done ? (
            <>
              <button
                onClick={() => setPlaying((p) => !p)}
                className="px-4 py-1.5 rounded bg-ocean-700 hover:bg-ocean-600 transition text-sm"
              >
                {playing ? '⏸ Pause' : '▶ Lire'}
              </button>
              <button
                onClick={() => setStep((s) => Math.min(events.length, s + 1))}
                className="px-4 py-1.5 rounded bg-ocean-700 hover:bg-ocean-600 transition text-sm"
              >
                ⏭ Pas à pas
              </button>
              <button
                onClick={() => {
                  setPlaying(false)
                  setStep(events.length)
                }}
                className="px-4 py-1.5 rounded bg-ocean-700 hover:bg-ocean-600 transition text-sm ml-auto"
              >
                Passer ⏩
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="ml-auto px-5 py-1.5 rounded bg-sand-300 text-ocean-900 font-display hover:bg-sand-100 transition"
            >
              Fermer
            </button>
          )}
        </footer>
      </div>
    </div>
  )
}

function EventRow({
  event,
  perspective,
  isLatest,
}: {
  event: CombatEventDto
  perspective: Perspective
  isLatest: boolean
}) {
  const isViewer = event.side === perspective
  // Two-tone scheme: the opponent's ACTIONS are a warm warning (orange), while
  // actual LOSSES (💀 sunk) use the app-wide alert red — see the sunk lines below.
  // orange-400 (saturé), pas orange-300 : trop proche du sand-300 du camp du
  // joueur sur fond ocean (retour user) — le contraste doit se voir d'un coup d'œil.
  const sideColor = isViewer ? 'text-sand-300' : 'text-orange-400'
  const headline = narrate(event, perspective)

  // Pure ricochet: shots fired, nothing pierced — the teaching moment for armure.
  const ricochet = event.wasted > 0 && event.sunk.length === 0 && event.damaged.length === 0

  return (
    <div
      className={`rounded-lg border p-3 transition-all duration-300 ${
        isLatest ? 'border-sand-300/50 bg-ocean-700/50' : 'border-ocean-500/20 bg-ocean-900/30'
      }`}
    >
      <p className={`text-sm ${sideColor}`}>{headline}</p>

      {(event.sunk.length > 0 || event.damaged.length > 0) && (
        <div className="mt-1 space-y-0.5">
          {event.sunk.map((name) => (
            <p key={name} className="text-xs text-red-400 font-medium">💀 <strong>{name}</strong> sombre !</p>
          ))}
          {event.damaged.map((d) => (
            <p key={d.name} className="text-xs text-sand-200">
              🔥 {d.name} encaisse {d.damage} coup{d.damage > 1 ? 's' : ''}
            </p>
          ))}
        </div>
      )}

      {/* Ton avertissement (l'ancien rum-400 n'existe pas dans la palette custom → était ignoré). */}
      {ricochet && (
        <p className="mt-1 text-xs text-orange-400 italic">
          ⚠️ Les boulets ricochent sur le blindage : coque trop résistante pour cette flotte.
        </p>
      )}

      {/* Discreet resolution detail (the dice that decided it). */}
      {event.dice > 0 && (
        <p className="mt-1 text-[10px] text-ocean-400 tabular-nums">
          🎲 {event.dice} dé{event.dice > 1 ? 's' : ''} · {event.hits} au but
        </p>
      )}

      {/* Rerolls (Tour de guet / Forge) — show that the relances actually acted. */}
      {(event.rerolls ?? 0) > 0 && (
        <p className="mt-0.5 text-[10px] text-purple-300/80 tabular-nums">
          ↻ {event.rerolls} relance{(event.rerolls ?? 0) > 1 ? 's' : ''}
          {(event.rerollHits ?? 0) > 0 ? ` · ${event.rerollHits} au but` : ''}
        </p>
      )}
    </div>
  )
}

/** Plain-language headline for a combat event — the naval story, not the dice. */
function narrate(event: CombatEventDto, perspective: Perspective): string {
  const side = sideLabel(event.side, perspective)

  if (event.phase === 'manoeuvre') {
    return event.hits > 0
      ? `${side} manœuvre et prend l'avantage du vent.`
      : `${side} reste sous le vent.`
  }

  const verb = event.phase === 'bordee' ? 'lâche une bordée' : 'monte à l\'abordage'
  if (event.sunk.length > 0) {
    return `${side} ${verb}. Coup fatal !`
  }
  if (event.damaged.length > 0) {
    return `${side} ${verb} et touche au but.`
  }
  if (event.wasted > 0) {
    return `${side} ${verb}… sans percer le blindage.`
  }
  return `${side} ${verb}, mais manque sa cible.`
}

function FleetOutcome({ combat, perspective }: { combat: CombatReplayDto; perspective: Perspective }) {
  const attackerAlive = combat.attackerSurvivors.filter((s) => !s.sunk).length
  const defenderAlive = combat.defenderSurvivors.filter((s) => !s.sunk).length
  const viewerIsAttacker = perspective === 'attacker'

  // Viewer's own column first, opponent second — labelled from the viewer's seat.
  const yourCol = viewerIsAttacker
    ? { title: 'Ta flotte', members: combat.attackerSurvivors, alive: attackerAlive }
    : { title: 'Ta défense', members: combat.defenderSurvivors, alive: defenderAlive }
  const oppCol = viewerIsAttacker
    ? { title: 'La défense', members: combat.defenderSurvivors, alive: defenderAlive }
    : { title: 'La flotte adverse', members: combat.attackerSurvivors, alive: attackerAlive }

  return (
    <div className="grid grid-cols-2 gap-3 mt-3 text-left">
      <FleetColumn title={yourCol.title} members={yourCol.members} alive={yourCol.alive} />
      <FleetColumn title={oppCol.title} members={oppCol.members} alive={oppCol.alive} />
    </div>
  )
}

function FleetColumn({
  title,
  members,
  alive,
}: {
  title: string
  members: CombatReplayDto['attackerSurvivors']
  alive: number
}) {
  return (
    <div className="bg-ocean-900/40 rounded p-2">
      <p className="text-xs text-ocean-300 mb-1">
        {title} <span className="text-ocean-400">({alive}/{members.length})</span>
      </p>
      <ul className="space-y-0.5">
        {members.map((m, i) => (
          <li
            key={i}
            className={`text-[11px] flex items-center justify-between gap-2 ${
              m.sunk ? 'text-red-400 line-through' : 'text-ocean-100'
            }`}
          >
            <span className="truncate">{m.sunk ? '💀' : '⛵'} {m.name}</span>
            {!m.sunk && <span className="text-ocean-400 tabular-nums shrink-0">{m.hp}/{m.maxHp}🛡</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}
